import { usePopoverState, Typography, PopoverDisclosure, Stack, Checkbox, colors } from '@ui-components/neo.tax';
import { ReactComponent as ColumnsIcon } from '@ui-components/neo.tax/dist/icons/columns.svg';
import { ReactComponent as SelectorIcon } from '@ui-components/neo.tax/dist/icons/selector.svg';
import React, { ReactElement } from 'react';

import { StyledDropdownButton, StyledPopover } from './column-visibility-popover.styled';
import { ColumnVisibilityState } from './column-visibility-popover.types';
import { useColumnVisibilityAnalyticIds } from './hooks/useColumnVisibilityAnalyticIds';

type ColumnVisibilityPopoverProps<ColumnName extends string> = ColumnVisibilityState<ColumnName>;

export const ColumnVisibilityPopover = <ColumnName extends string>({
  alwaysVisibleColumns,
  columns,
  onColumnVisibilityChange,
  visibleColumns,
}: ColumnVisibilityPopoverProps<ColumnName>): ReactElement => {
  const { menuButtonAnalyticId, selectAllCheckboxAnalyticId } = useColumnVisibilityAnalyticIds();
  const state = usePopoverState({ gutter: 6, placement: 'bottom-start' });

  const handleSelectAll = (): void => {
    const allColumnsVisible = visibleColumns.length === columns.length;
    const updatedVisibleColumns = allColumnsVisible ? alwaysVisibleColumns : columns.map((column) => column.name);
    onColumnVisibilityChange(updatedVisibleColumns);
  };

  const handleColumnToggle = (columnName: ColumnName): void => {
    const isVisible = visibleColumns.includes(columnName);
    const updatedVisibleColumns = isVisible
      ? visibleColumns.filter((col) => col !== columnName)
      : [...visibleColumns, columnName];
    onColumnVisibilityChange(updatedVisibleColumns);
  };

  return (
    <>
      <StyledDropdownButton
        id={menuButtonAnalyticId}
        as={PopoverDisclosure}
        state={state}
        leadingIcon={<ColumnsIcon color={colors.gray[400]} />}
        trailingIcon={<SelectorIcon />}
        placeholder="Columns"
        value={
          visibleColumns.length === columns.length ? (
            <Typography as="span" fontSize="sm" color={colors.gray[500]}>
              All columns visible
            </Typography>
          ) : (
            <Typography as="span" fontSize="sm" color={colors.gray[500]}>
              {columns.length - visibleColumns.length} Hidden:{' '}
              {columns
                .filter((column) => !visibleColumns.includes(column.name))
                .map((column) => column.label)
                .join(', ')}
            </Typography>
          )
        }
      />
      <StyledPopover state={state} label="Column visibility popover">
        <Stack direction="column" gap={2.5}>
          <Typography as="span" fontSize="sm" fontWeight="normal" color={colors.gray[400]}>
            Toggle column visibility
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Checkbox
              id={selectAllCheckboxAnalyticId}
              name="show-all-columns"
              checked={visibleColumns.length === columns.length}
              onChange={handleSelectAll}
            />
            <Typography as="span">Show all columns</Typography>
          </Stack>
          <hr />
          {columns
            .filter((column) => !alwaysVisibleColumns.includes(column.name))
            .map((column) => (
              <Stack key={column.name} direction="row" alignItems="center" gap={1}>
                <Checkbox
                  id={column.name}
                  name={column.name}
                  checked={visibleColumns.includes(column.name)}
                  onChange={(): void => handleColumnToggle(column.name)}
                />
                <Typography as="span">{column.label}</Typography>
              </Stack>
            ))}
        </Stack>
      </StyledPopover>
    </>
  );
};
